import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
    addGroupMembers,
    createGroup,
    getGroups,
    getGroupMembers,
    getUsers,
    removeGroupMembers,
    requestGroupDelete,
    updateGroup,
    uploadFile,
} from '@/lib/api';

interface SalonControlProps {
    theme: any;
}

export function SalonControl({ theme }: SalonControlProps) {
    const [users, setUsers] = useState<any[]>([]);
    const [groups, setGroups] = useState<any[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
    const [groupMembers, setGroupMembers] = useState<any[]>([]);
    const [groupMemberIds, setGroupMemberIds] = useState<Set<number>>(new Set());
    const [filterDisplay, setFilterDisplay] = useState('');
    const [filterLoginId, setFilterLoginId] = useState('');
    const [filterEmail, setFilterEmail] = useState('');
    const [filterRoom, setFilterRoom] = useState('');
    const [filterCreatedAt, setFilterCreatedAt] = useState('');
    const [createName, setCreateName] = useState('');
    const [createGroupType, setCreateGroupType] = useState<'normal' | 'announcement'>('normal');
    const [createIcon, setCreateIcon] = useState('');
    const [createBackground, setCreateBackground] = useState('');
    const [createUploading, setCreateUploading] = useState(false);
    const [editGroupId, setEditGroupId] = useState<number | null>(null);
    const [editName, setEditName] = useState('');
    const [editIcon, setEditIcon] = useState('');
    const [editBackground, setEditBackground] = useState('');
    const [editUploading, setEditUploading] = useState(false);
    const [message, setMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState<'create' | 'invite' | 'remove' | null>(null);
    const [confirmUserIds, setConfirmUserIds] = useState<number[]>([]);
    const [removeKeepLogs, setRemoveKeepLogs] = useState(true);

    const fetchUsers = async () => {
        setLoadingUsers(true);
        try {
            const pageSize = 200;
            let skip = 0;
            let allUsers: any[] = [];

            while (true) {
                const res = await getUsers({ skip, limit: pageSize });
                const batch = Array.isArray(res.data) ? res.data : [];
                allUsers = allUsers.concat(batch);
                if (batch.length < pageSize) {
                    break;
                }
                skip += pageSize;
            }

            setUsers(allUsers);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingUsers(false);
        }
    };

    const fetchGroups = async () => {
        setLoadingGroups(true);
        try {
            const res = await getGroups();
            setGroups(res.data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingGroups(false);
        }
    };

    const fetchGroupMembers = async (groupId: number) => {
        try {
            const res = await getGroupMembers(groupId);
            const members = res.data || [];
            setGroupMembers(members);
            setGroupMemberIds(new Set(members.map((member: any) => member.user_id)));
        } catch (err) {
            console.error(err);
            setGroupMembers([]);
            setGroupMemberIds(new Set());
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchGroups();
    }, []);

    const filteredUsers = useMemo(() => {
        const display = filterDisplay.trim().toLowerCase();
        const loginId = filterLoginId.trim().toLowerCase();
        const email = filterEmail.trim().toLowerCase();
        const room = filterRoom.trim().toLowerCase();
        const created = filterCreatedAt.trim();
        return users.filter((user) => {
            const displayName = (user.display_name || '').toLowerCase();
            const usernameValue = (user.username || '').toLowerCase();
            const emailValue = (user.email || '').toLowerCase();
            const roomValue = (user.room_no || '').toLowerCase();
            const createdValue = user.created_at
                ? new Date(user.created_at).toISOString().slice(0, 10)
                : '';
            if (display && !displayName.includes(display)) return false;
            if (loginId && !usernameValue.includes(loginId)) return false;
            if (email && !emailValue.includes(email)) return false;
            if (room && !roomValue.includes(room)) return false;
            if (created && !createdValue.includes(created)) return false;
            return true;
        });
    }, [users, filterDisplay, filterLoginId, filterEmail, filterRoom, filterCreatedAt]);

    const toggleUser = (userId: number) => {
        setSelectedUserIds((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) {
                next.delete(userId);
            } else {
                next.add(userId);
            }
            return next;
        });
    };

    const handleUpload = async (file: File, target: 'createIcon' | 'createBackground' | 'editIcon' | 'editBackground') => {
        if (!file) return;
        if (target.startsWith('create')) {
            setCreateUploading(true);
        } else {
            setEditUploading(true);
        }
        try {
            const res = await uploadFile(file);
            const url = res.data?.url || res.data?.path || res.data?.file_url || res.data?.file_path || res.data?.avatar_path || res.data;
            if (typeof url === 'string') {
                if (target === 'createIcon') setCreateIcon(url);
                if (target === 'createBackground') setCreateBackground(url);
                if (target === 'editIcon') setEditIcon(url);
                if (target === 'editBackground') setEditBackground(url);
            }
        } catch (err) {
            console.error(err);
        } finally {
            if (target.startsWith('create')) {
                setCreateUploading(false);
            } else {
                setEditUploading(false);
            }
        }
    };

    const requestCreateGroup = () => {
        if (!createName.trim()) return;
        setConfirmAction('create');
        setConfirmUserIds(Array.from(selectedUserIds));
    };

    const confirmCreateGroup = async () => {
        setMessage('');
        try {
            await createGroup({
                group_name: createName.trim(),
                group_type: createGroupType,
                icon_path: createIcon || null,
                background_path: createBackground || null,
                member_ids: confirmUserIds,
            });
            setCreateName('');
            setCreateGroupType('normal');
            setCreateIcon('');
            setCreateBackground('');
            setSelectedUserIds(new Set());
            setMessage('グループを作成しました。');
            fetchGroups();
        } catch (err: any) {
            setMessage(err?.response?.data?.detail || '作成に失敗しました。');
        }
    };

    const handleSelectGroup = (group: any) => {
        setEditGroupId(group.group_id);
        setEditName(group.group_name || '');
        setEditIcon(group.icon_path || '');
        setEditBackground(group.background_path || '');
        fetchGroupMembers(group.group_id);
        setMessage('');
    };

    const handleUpdateGroup = async () => {
        if (!editGroupId) return;
        setMessage('');
        try {
            await updateGroup(editGroupId, {
                group_name: editName.trim() || undefined,
                icon_path: editIcon || null,
                background_path: editBackground || null,
            });
            setMessage('グループ情報を更新しました。');
            fetchGroups();
        } catch (err: any) {
            setMessage(err?.response?.data?.detail || '更新に失敗しました。');
        }
    };

    const handleInviteMembers = async () => {
        if (!editGroupId) return;
        const memberIds = Array.from(selectedUserIds).filter((id) => !groupMemberIds.has(id));
        if (memberIds.length === 0) {
            setMessage('追加できるユーザーが選択されていません。');
            return;
        }
        setConfirmAction('invite');
        setConfirmUserIds(memberIds);
    };

    const confirmInviteMembers = async () => {
        if (!editGroupId) return;
        setMessage('');
        try {
            await addGroupMembers(editGroupId, confirmUserIds);
            setMessage('選択したユーザーを招待しました。');
            fetchGroupMembers(editGroupId);
        } catch (err: any) {
            setMessage(err?.response?.data?.detail || '招待に失敗しました。');
        }
    };

    const handleRemoveMembers = async () => {
        if (!editGroupId) return;
        const memberIds = Array.from(selectedUserIds).filter((id) => groupMemberIds.has(id));
        if (memberIds.length === 0) {
            setMessage('排除できるユーザーが選択されていません。');
            return;
        }
        setConfirmAction('remove');
        setConfirmUserIds(memberIds);
        setRemoveKeepLogs(true);
    };

    const confirmRemoveMembers = async () => {
        if (!editGroupId) return;
        setMessage('');
        try {
            await removeGroupMembers(editGroupId, confirmUserIds, removeKeepLogs);
            setMessage(removeKeepLogs ? '選択したユーザーを排除しました。' : '選択したユーザーを排除し、トークログを削除しました。');
            fetchGroupMembers(editGroupId);
        } catch (err: any) {
            setMessage(err?.response?.data?.detail || '排除に失敗しました。');
        }
    };

    const handleDeleteGroup = async () => {
        if (!editGroupId) return;
        setMessage('');
        try {
            const res = await requestGroupDelete(editGroupId);
            const data = res.data;
            if (data.status === 'deleted') {
                setMessage('グループを削除しました。');
            } else {
                const remaining = Math.max(0, (data.required || 3) - (data.votes || 0));
                setMessage(`削除にはあと${remaining}人の承認が必要です。`);
            }
            fetchGroups();
        } catch (err: any) {
            setMessage(err?.response?.data?.detail || '削除に失敗しました。');
        }
    };

    const nonDeletedGroups = groups.filter((group) => !group.is_deleted && String(group.group_type || 'normal').toLowerCase() !== 'direct');
    const userById = useMemo(() => new Map(users.map((user) => [user.user_id, user])), [users]);
    const confirmUsers = confirmUserIds
        .map((id) => userById.get(id))
        .filter(Boolean);
    const confirmTitle =
        confirmAction === 'create'
            ? 'グループ作成の確認'
            : confirmAction === 'invite'
                ? 'ユーザー招待の確認'
                : 'ユーザー排除の確認';
    const confirmDescription =
        confirmAction === 'create'
            ? '以下のユーザーをグループに追加します。'
            : confirmAction === 'invite'
                ? '以下のユーザーを招待します。'
                : '以下のユーザーを排除します。';

    return (
        <div className="flex flex-col h-full min-h-0 overflow-auto md:overflow-hidden" style={{ color: theme?.colors?.text || '#000' }}>
            <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200 bg-white">
                <h2 className="text-lg font-bold">Salon Control</h2>
                <p className="text-xs text-gray-500">グループ作成・招待・編集・削除を管理できます。</p>
            </div>

            <div className="flex flex-col md:flex-row md:flex-1 md:min-h-0 md:overflow-hidden">
                <div className="w-full md:w-2/5 lg:w-1/3 md:border-r border-gray-200 bg-white flex flex-col">
                    <div className="p-3 md:p-4 border-b border-gray-100">
                        <div className="font-semibold text-sm mb-2">ユーザー絞り込み</div>
                        <div className="space-y-2">
                            <input
                                className="w-full p-2 border rounded text-xs"
                                placeholder="表示名で検索"
                                value={filterDisplay}
                                onChange={(e) => setFilterDisplay(e.target.value)}
                            />
                            <input
                                className="w-full p-2 border rounded text-xs"
                                placeholder="ログインIDで検索"
                                value={filterLoginId}
                                onChange={(e) => setFilterLoginId(e.target.value)}
                            />
                            <input
                                className="w-full p-2 border rounded text-xs"
                                placeholder="メールアドレスで検索"
                                value={filterEmail}
                                onChange={(e) => setFilterEmail(e.target.value)}
                            />
                            <input
                                className="w-full p-2 border rounded text-xs"
                                placeholder="RoomNoで検索"
                                value={filterRoom}
                                onChange={(e) => setFilterRoom(e.target.value)}
                            />
                            <input
                                className="w-full p-2 border rounded text-xs"
                                placeholder="登録日時"
                                type="date"
                                value={filterCreatedAt}
                                onChange={(e) => setFilterCreatedAt(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="md:flex-1 md:overflow-y-auto p-3 md:p-4 space-y-2">
                        {loadingUsers ? (
                            <div className="text-xs text-gray-500">読み込み中...</div>
                        ) : (
                            filteredUsers.map((user) => {
                                const isMember = groupMemberIds.has(user.user_id);
                                return (
                                <label key={user.user_id} className="flex items-center gap-2 text-xs">
                                    <input
                                        type="checkbox"
                                        checked={selectedUserIds.has(user.user_id)}
                                        onChange={() => toggleUser(user.user_id)}
                                    />
                                    <span className={`font-medium ${isMember ? 'text-red-600' : ''}`}>{user.display_name || '-'}</span>
                                    <span className={`${isMember ? 'text-red-500' : 'text-gray-500'}`}>({user.room_no || '-'})</span>
                                    {isMember && <span className="text-red-500">参加中</span>}
                                </label>
                            )})
                        )}
                    </div>
                </div>

                <div className="w-full mt-4 md:mt-0 md:flex-1 md:min-h-0 md:overflow-y-auto p-4 md:p-6 space-y-6 bg-white md:bg-transparent">
                    {message && (
                        <div className="text-sm text-[#800020] font-semibold">{message}</div>
                    )}

                    <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
                        <div className="font-semibold">グループ作成</div>
                        <input
                            className="w-full p-2 border rounded text-sm"
                            placeholder="グループ名"
                            value={createName}
                            onChange={(e) => setCreateName(e.target.value)}
                        />
                        <div>
                            <div className="text-xs text-gray-500 mb-1">チャットタイプ</div>
                            <select
                                className="w-full p-2 border rounded text-sm"
                                value={createGroupType}
                                onChange={(e) => setCreateGroupType(e.target.value === 'announcement' ? 'announcement' : 'normal')}
                            >
                                <option value="normal">通常チャット</option>
                                <option value="announcement">アナウンスチャット（閲覧のみ）</option>
                            </select>
                            <div className="text-[11px] text-gray-400 mt-1">
                                アナウンスは Manager / Maestro のみ投稿できます。
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <div className="text-xs text-gray-500 mb-1">グループアイコン</div>
                                {createIcon && <img src={createIcon} alt="icon" className="w-16 h-16 rounded-full object-cover mb-2" />}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => e.target.files && handleUpload(e.target.files[0], 'createIcon')}
                                    className="text-xs"
                                />
                            </div>
                            <div>
                                <div className="text-xs text-gray-500 mb-1">背景画像</div>
                                {createBackground && <img src={createBackground} alt="background" className="w-full h-20 rounded object-cover mb-2" />}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => e.target.files && handleUpload(e.target.files[0], 'createBackground')}
                                    className="text-xs"
                                />
                            </div>
                        </div>
                        <Button
                            onClick={requestCreateGroup}
                            disabled={createUploading || !createName.trim()}
                            className="w-full"
                            style={{ backgroundColor: theme?.colors?.primary || '#800020', color: '#fff' }}
                        >
                            {createUploading ? 'アップロード中...' : 'グループを作成'}
                        </Button>
                    </div>

                    <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
                        <div className="font-semibold">既存グループの編集・招待</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2 max-h-64 overflow-y-auto border rounded p-2">
                                {loadingGroups ? (
                                    <div className="text-xs text-gray-500">読み込み中...</div>
                                ) : (
                                    nonDeletedGroups.map((group) => (
                                        <button
                                            key={group.group_id}
                                            onClick={() => handleSelectGroup(group)}
                                            className={`w-full text-left text-sm px-2 py-1 rounded ${
                                                editGroupId === group.group_id ? 'bg-yellow-50 border border-yellow-400' : 'hover:bg-gray-50'
                                            }`}
                                        >
                                            {group.group_name}
                                        </button>
                                    ))
                                )}
                            </div>
                            <div className="space-y-3">
                                <input
                                    className="w-full p-2 border rounded text-sm"
                                    placeholder="グループ名"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    disabled={!editGroupId}
                                />
                                <div className="text-xs text-gray-500">グループアイコン</div>
                                {editIcon && <img src={editIcon} alt="icon" className="w-16 h-16 rounded-full object-cover" />}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => e.target.files && handleUpload(e.target.files[0], 'editIcon')}
                                    className="text-xs"
                                    disabled={!editGroupId}
                                />
                                <div className="text-xs text-gray-500">背景画像</div>
                                {editBackground && <img src={editBackground} alt="background" className="w-full h-20 rounded object-cover" />}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => e.target.files && handleUpload(e.target.files[0], 'editBackground')}
                                    className="text-xs"
                                    disabled={!editGroupId}
                                />
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                onClick={handleUpdateGroup}
                                disabled={!editGroupId || editUploading}
                            >
                                {editUploading ? 'アップロード中...' : '更新する'}
                            </Button>
                            <Button
                                onClick={handleInviteMembers}
                                disabled={!editGroupId || selectedUserIds.size === 0}
                                variant="outline"
                            >
                                選択ユーザーを招待
                            </Button>
                            <Button
                                onClick={handleRemoveMembers}
                                disabled={!editGroupId || selectedUserIds.size === 0}
                                variant="outline"
                                className="border-red-400 text-red-600 hover:bg-red-50"
                            >
                                選択ユーザーを排除
                            </Button>
                            <Button
                                onClick={handleDeleteGroup}
                                disabled={!editGroupId}
                                variant="destructive"
                            >
                                グループ削除
                            </Button>
                        </div>
                        <div className="border rounded p-3 bg-gray-50 space-y-2">
                            <div className="text-xs font-semibold text-gray-600">参加中ユーザー</div>
                            {!editGroupId ? (
                                <div className="text-xs text-gray-500">グループを選択してください。</div>
                            ) : groupMembers.length === 0 ? (
                                <div className="text-xs text-gray-500">参加者がいません。</div>
                            ) : (
                                <div className="flex flex-wrap gap-2">
                                    {groupMembers.map((member) => (
                                        <span key={member.user_id} className="text-xs bg-white border rounded-full px-2 py-1">
                                            {member.display_name?.trim() || member.room_no || '住人'} ({member.room_no || '-'})
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="text-xs text-gray-500">
                            Manager/Maestro の削除は 3名以上の承認で実行されます。Admin は単独で削除できます。
                        </div>
                    </div>
                </div>
            </div>
            {confirmAction && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-5 space-y-4">
                        <div className="text-lg font-semibold">{confirmTitle}</div>
                        <div className="text-sm text-gray-600">{confirmDescription}</div>
                        <div className="max-h-48 overflow-y-auto border rounded p-3 bg-gray-50 space-y-1 text-sm">
                            {confirmUsers.length === 0 ? (
                                <div className="text-gray-500">対象ユーザーがいません。</div>
                            ) : (
                                confirmUsers.map((user: any) => (
                                    <div key={user.user_id}>
                                        {user.display_name?.trim() || user.room_no || '住人'} ({user.room_no || '-'})
                                    </div>
                                ))
                            )}
                        </div>
                        {confirmAction === 'remove' && (
                            <div className="space-y-2 rounded border bg-white p-3 text-sm">
                                <div className="text-xs font-semibold text-gray-600">トークログの扱い</div>
                                <label className="flex items-start gap-2">
                                    <input
                                        type="radio"
                                        name="remove-logs"
                                        checked={removeKeepLogs}
                                        onChange={() => setRemoveKeepLogs(true)}
                                    />
                                    <div>
                                        <div className="font-medium">トークログを残す</div>
                                        <div className="text-xs text-gray-500">イベント終了などで履歴を残したい場合</div>
                                    </div>
                                </label>
                                <label className="flex items-start gap-2">
                                    <input
                                        type="radio"
                                        name="remove-logs"
                                        checked={!removeKeepLogs}
                                        onChange={() => setRemoveKeepLogs(false)}
                                    />
                                    <div>
                                        <div className="font-medium text-red-600">トークログを削除する</div>
                                        <div className="text-xs text-gray-500">対象ユーザーのメッセージを非表示にします</div>
                                    </div>
                                </label>
                            </div>
                        )}
                        <div className="flex items-center justify-end gap-2">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setConfirmAction(null);
                                    setConfirmUserIds([]);
                                }}
                            >
                                キャンセル
                            </Button>
                            <Button
                                onClick={async () => {
                                    if (confirmAction === 'create') {
                                        await confirmCreateGroup();
                                    } else if (confirmAction === 'invite') {
                                        await confirmInviteMembers();
                                    } else {
                                        await confirmRemoveMembers();
                                    }
                                    setConfirmAction(null);
                                    setConfirmUserIds([]);
                                }}
                            >
                                実行する
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
